import React, { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import './AdminSidebar.scss'
import { useAuth } from '../../../Components/AuthProvider/AuthProvider'
import { useAuth0 } from '@auth0/auth0-react'
import {
  FaTachometerAlt,
  FaUsers,
  FaGasPump,
  FaExchangeAlt,
  FaTicketAlt,
  FaBalanceScale,
  FaGift,
  FaCreditCard,
  FaShoppingCart,
  FaExclamationTriangle,
  FaUserTie,
  FaMoneyBillWave,
  FaBell,
  FaCog,
  FaArrowLeft,
  FaMapMarkerAlt,
} from 'react-icons/fa'

import logo from '../../Assets/Icon/Sidebar/logo.svg'
import profileCircle from '../../Assets/Icon/Sidebar/profile-circle-white.svg'
import arrowDown from '../../Assets/Icon/Sidebar/arrow-down-white.svg'

interface AdminSidebarProps {
  onNavigate?: () => void
}

const AdminSidebar = ({ onNavigate }: AdminSidebarProps) => {
  const navigate = useNavigate()
  const [isSwitching, setIsSwitching] = React.useState(false)
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { user, logout } = useAuth()
  const { logout: auth0Logout } = useAuth0()

  // Get user information from auth context
  const getUserDisplayName = () => {
    if (!user) return 'Guest User'

    // Try to get name from different possible fields
    if (user.user?.name) return user.user.name
    if (user.user?.firstName && user.user?.lastName) {
      return `${user.user.firstName} ${user.user.lastName}`
    }
    if (user.user?.firstName) return user.user.firstName
    if (user.name) return user.name
    if (user.email) return user.email

    return 'Admin User'
  }

  const getUserRole = () => {
    if (!user) return 'Guest'

    // Check for different role fields
    if (user.loanProviderAdminRole) return 'Loan Provider Admin'
    if (user.user?.role) return user.user.role
    if (user.user?.adminRole) return user.user.adminRole

    return 'Super Admin'
  }

  const getUserAvatar = () => {
    if (!user) return profileCircle

    // Try to get user picture from different fields
    if (user.user?.picture) return user.user.picture
    if (user.picture) return user.picture

    return profileCircle
  }

  const handleLogout = () => {
    setIsProfileDropdownOpen(false)
    if (user?.user?.name) {
      auth0Logout({ logoutParams: { returnTo: window.location.origin } })
      logout()
    } else {
      localStorage.removeItem('__TG')
      window.location.href = '/login'
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false)
      }
    }

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isProfileDropdownOpen])

  return (
    <>
      <nav className='bx-sidebar' aria-label='BoostXpress Admin Navigation'>
        <div className='bx-sidebar__brand'>
          <button
            type='button'
            className='bx-sidebar__close'
            aria-label='Close navigation menu'
            onClick={onNavigate}
          >
            <span />
            <span />
          </button>
          <img src={logo} alt='BoostXpress' className='bx-sidebar__logo' />
        </div>

        <div className='bx-sidebar__scrollable'>
          <div className='bx-sidebar__section'>
            <div className='bx-sidebar__section-title'>OVERVIEW</div>
            <NavLink
              to='/boostxpress/dashboard'
              onClick={onNavigate}
              className={({ isActive }) =>
                `bx-sidebar__item ${isActive ? 'bx-sidebar__item--active' : ''}`
              }
            >
              <div className='bx-sidebar__item-icon-wrapper'>
                <span className='bx-sidebar__item-icon'><FaTachometerAlt className='bx-sidebar__item-icon' /></span>
                <div className='bx-sidebar__item-indicator'></div>
              </div>
              <span className='bx-sidebar__item-label'>Dashboard</span>
            </NavLink>
          </div>

          <div className='bx-sidebar__section'>
            <div className='bx-sidebar__section-title'>MANAGEMENT</div>
            {[
              { label: 'Users', to: '/boostxpress/users', icon: <FaUsers className='bx-sidebar__item-icon' /> },
              { label: 'Stations', to: '/boostxpress/stations', icon: <FaGasPump className='bx-sidebar__item-icon' /> },
              { label: 'Transactions', to: '/boostxpress/transactions', icon: <FaExchangeAlt className='bx-sidebar__item-icon' /> },
              { label: 'Vouchers', to: '/boostxpress/vouchers', icon: <FaTicketAlt className='bx-sidebar__item-icon' /> },
              { label: 'Price Match', to: '/boostxpress/price-match', icon: <FaBalanceScale className='bx-sidebar__item-icon' /> },
              { label: 'Incentives', to: '/boostxpress/referrals', icon: <FaGift className='bx-sidebar__item-icon' /> },
              { label: 'Boost Flex', to: '/boostxpress/boost-flex', icon: <FaCreditCard className='bx-sidebar__item-icon' /> },
              { label: 'Promo Stations', to: '/boostxpress/boost-promo-stations', icon: <FaMapMarkerAlt className='bx-sidebar__item-icon' /> },
              { label: 'Orders', to: '/boostxpress/orders', icon: <FaShoppingCart className='bx-sidebar__item-icon' /> },
              { label: 'Order Disputes', to: '/boostxpress/order-disputes', icon: <FaExclamationTriangle className='bx-sidebar__item-icon' /> },
              { label: 'Attendants', to: '/boostxpress/attendants', icon: <FaUserTie className='bx-sidebar__item-icon' /> },
              { label: 'Payouts', to: '/boostxpress/payouts', icon: <FaMoneyBillWave className='bx-sidebar__item-icon' /> },
            ].map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `bx-sidebar__item ${isActive ? 'bx-sidebar__item--active' : ''}`
                }
              >
                <span className='bx-sidebar__item-icon'>{item.icon}</span>
                <span className='bx-sidebar__item-label'>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className='bx-sidebar__section'>
            <div className='bx-sidebar__section-title'>SUPPORT & GROWTH</div>
            {[
              // { label: 'Support Tickets', to: '/boostxpress/support' },
              { label: 'Push Notifications', to: '/boostxpress/notifications', icon: <FaBell className='bx-sidebar__item-icon' /> },
              { label: 'App Config', to: '/boostxpress/app-config', icon: <FaCog className='bx-sidebar__item-icon' /> },
            ].map((item) => (
              <NavLink
                key={item.label}
                to={item.to}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `bx-sidebar__item ${isActive ? 'bx-sidebar__item--active' : ''}`
                }
              >
                <span className='bx-sidebar__item-icon'>{item.icon}</span>
                <span className='bx-sidebar__item-label'>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className='bx-sidebar__section'>
            <button
              type='button'
              className='bx-sidebar__item bx-sidebar__item--back'
              disabled={isSwitching}
              onClick={() => {
                setIsSwitching(true)
                window.setTimeout(() => {
                  navigate('/dashboard', { replace: true })
                }, 450)
              }}
            >
              <span className='bx-sidebar__item-icon'><FaArrowLeft className='bx-sidebar__item-icon' /></span>
              <span className='bx-sidebar__item-label'>Back to Main Admin</span>
            </button>
          </div>
        </div>

        <div className='bx-sidebar__footer'>
          <div className='bx-sidebar__profile-section' ref={dropdownRef}>
            <div
              className='bx-sidebar__profile-trigger'
              onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            >
              <div className='bx-sidebar__profile-avatar'>
                <img
                  src={getUserAvatar()}
                  alt='Profile'
                  className='bx-sidebar__avatar-image'
                  onError={(e) => {
                    e.currentTarget.src = profileCircle
                  }}
                />
              </div>
              <div className='bx-sidebar__profile-info'>
                <div className='bx-sidebar__profile-name'>{getUserDisplayName()}</div>
                <div className='bx-sidebar__profile-role'>{getUserRole()}</div>
              </div>
              <div className={`bx-sidebar__dropdown-arrow ${isProfileDropdownOpen ? 'open' : ''}`}>
                <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                  <path d='M4 6L8 10L12 6' stroke='white' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
              </div>
            </div>

            {isProfileDropdownOpen && (
              <div className='bx-sidebar__profile-dropdown'>
                <div className='bx-sidebar__dropdown-item' onClick={handleLogout}>
                  <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
                    <path d='M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M16 17L21 12L16 7' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                    <path d='M21 12H9' stroke='#516F93' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
                  </svg>
                  <span>Logout</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {isSwitching ? (
        <div className='admin-switching-overlay' role='status' aria-live='polite'>
          <div className='admin-switching-overlay__card'>
            <div className='spinner-border' aria-hidden='true' />
            <div className='admin-switching-overlay__text'>Switching, please wait...</div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default AdminSidebar
